import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { Sparkle, CircleNotch, Warning, FilePdf } from "@phosphor-icons/react";

const SEV = { HIGH: "bg-rose-50 text-rose-700 border-rose-200", MEDIUM: "bg-amber-50 text-amber-700 border-amber-200", LOW: "bg-zinc-100 text-zinc-500 border-zinc-200" };
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

export function DossierPane({ clientId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["dossier", clientId], queryFn: () => api.get(`/dossier/${clientId}`).then((r) => r.data) });
  const gen = useMutation({
    mutationFn: () => api.post(`/dossier/${clientId}/generate`),
    onSuccess: () => { toast.success("Briefing dossier compiled"); qc.invalidateQueries({ queryKey: ["dossier", clientId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const d = data?.dossier;
  const c = d?.content;
  const exportPdf = async () => {
    try {
      const r = await api.get(`/dossier/${clientId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossier_${(d?.client_name || clientId).replace(/ /g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier PDF downloaded");
    } catch {
      toast.error("Compile a dossier first — nothing to export yet");
    }
  };
  return (
    <div data-testid="dossier-pane">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-500">Audit-ready pre-meeting synthesis: prior-year vs current-year, open gaps, due diligence, and focus alerts.</p>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="dossier-export-pdf-btn" size="sm" variant="outline" className="h-8 text-xs" disabled={!c} onClick={exportPdf}>
            <FilePdf size={13} className="mr-1" /> Export PDF
          </Button>
          <Button data-testid="dossier-generate-btn" size="sm" className="h-8 text-xs" disabled={gen.isPending} onClick={() => gen.mutate()}>
            {gen.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Sparkle size={13} className="mr-1" />}
            {c ? "Refresh dossier" : "Compile dossier"}
          </Button>
        </div>
      </div>
      {!c ? (
        <p className="text-xs text-zinc-400 italic">No dossier yet — compile one before the planning meeting.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dossier-content">
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2">Prior year vs current year</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div><p className="text-[9px] text-zinc-400">PY AGI</p><p className="font-mono text-sm text-zinc-900">{money(c.prior_year_summary?.adjusted_gross_income_py)}</p></div>
                <div><p className="text-[9px] text-zinc-400">PY Eff. rate</p><p className="font-mono text-sm text-zinc-900">{c.prior_year_summary?.effective_tax_rate_py != null ? `${Math.round(c.prior_year_summary.effective_tax_rate_py * 100)}%` : "—"}</p></div>
                <div><p className="text-[9px] text-zinc-400">CY docs</p><p className="font-mono text-sm text-zinc-900">{c.current_year_status?.total_documents_uploaded ?? "—"}</p></div>
              </div>
              <p className="text-[10px] text-zinc-500">PY schedules: {(c.prior_year_summary?.active_schedules_py || []).join(", ") || "—"}</p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2">Unresolved gaps ({(c.current_year_status?.unresolved_gaps_flagged || []).length})</p>
              {(c.current_year_status?.unresolved_gaps_flagged || []).map((g, i) => (
                <div key={i} className="border-b border-zinc-100 last:border-0 py-1.5">
                  <p className="text-xs text-zinc-800 flex items-center gap-1.5"><Warning size={12} className="text-amber-500" /> {g.label} <span className="text-[9px] text-zinc-400">({g.category})</span></p>
                  {g.client_notes && <p className="text-[10px] text-indigo-700 italic mt-0.5">Client: "{g.client_notes}"</p>}
                </div>
              ))}
              {!(c.current_year_status?.unresolved_gaps_flagged || []).length && <p className="text-xs text-emerald-700">No open gaps.</p>}
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2">Focus alerts</p>
              {(c.cpa_focus_alerts || []).map((a, i) => (
                <div key={i} className="mb-2 last:mb-0" data-testid={`dossier-alert-${i}`}>
                  <p className="text-xs text-zinc-800"><span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 mr-1.5 ${SEV[a.severity] || SEV.LOW}`}>{a.severity}</span><span className="font-medium">{a.metric}</span></p>
                  <p className="text-[11px] text-zinc-600 mt-0.5">{a.summary}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2">Due diligence record</p>
              {(c.irc_due_diligence_payload?.summary_lines || []).map((l, i) => <p key={i} className="text-[11px] text-zinc-700 mb-1">• {l}</p>)}
              {(c.irc_due_diligence_payload?.open_items || []).length > 0 && (
                <p className="text-[10px] text-amber-700 mt-1.5">Open: {(c.irc_due_diligence_payload.open_items || []).join("; ")}</p>
              )}
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2">Planning meeting agenda</p>
              {(c.meeting_agenda || []).map((m, i) => <p key={i} className="text-[11px] text-zinc-700 mb-1">{i + 1}. {m}</p>)}
            </div>
          </div>
        </div>
      )}
      {d && <p className="text-[9px] text-zinc-400 mt-3 font-mono">{d.id} · generated {new Date(d.generated_at).toLocaleString()} by {d.generated_by}</p>}
    </div>
  );
}
